import { Box } from '@chakra-ui/react';
import { Button } from 'components/v4';
import { differenceBy } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import TransferTable from './TransferTable';
import { TransferTableProvider } from './TransferTableProvider';

const BadgeWithCount = ({ count }) => {
    return (
        <div className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-brand-400 text-xs font-bold text-white dark:border-gray-900">
            {count}
        </div>
    );
};

const MoveButton = ({
    children,
    isDisabled,
    handleOnClick,
    dataWalkthroughId,
}) => {
    const { darkMode } = useSelector((state) => {
        return { ...state.settings };
    });

    return (
        <Button
            type="button"
            className="focus:ring-blue-30 relative inline-flex h-8 w-full items-center justify-center rounded-lg border-[1px] border-brand-500 p-3 text-center font-medium leading-7 text-brand-500 focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={isDisabled}
            bg={darkMode ? '#263238 !important' : 'inherit'}
            color={darkMode ? 'white' : 'inherit'}
            onClick={handleOnClick}
            data-walkthroughid={dataWalkthroughId}>
            {children}
        </Button>
    );
};

const TransferringTables = ({
    // Data props
    availableData = [],
    chosenData = [],

    // Table configuration
    availableTableConfig = {
        captionName: 'Available Items',
        placeholder: 'Search available items',
        colDefs: [],
        searchField: 'name',
    },
    chosenTableConfig = {
        captionName: 'Chosen Items',
        placeholder: 'Search chosen items',
        colDefs: [],
        searchField: 'name',
    },

    // Loading states
    isLoading = false,
    isDisabled = false,

    // Callbacks
    onAvailableDataChange,
    onChosenDataChange,
    onCheckedAvailableChange,
    onCheckedChosenChange,

    // Walkthrough IDs
    walkthroughIds = {
        moveToChosen: '/transfer/button/move/chosen',
        moveToAvailable: '/transfer/button/move/available',
        moveAllToChosen: '/transfer/button/move/chosen/all',
        moveAllToAvailable: '/transfer/button/move/available/all',
    },

    // Custom filter functions
    availableFilterFunction,
    chosenFilterFunction,
}) => {
    const [checkedAvailable, setCheckedAvailable] = useState([]);
    const [checkedChosen, setCheckedChosen] = useState([]);

    // Handle checked state changes
    const handleCheckedAvailableChange = (newChecked) => {
        setCheckedAvailable(newChecked);
        onCheckedAvailableChange?.(newChecked);
    };

    const handleCheckedChosenChange = (newChecked) => {
        setCheckedChosen(newChecked);
        onCheckedChosenChange?.(newChecked);
    };

    // Row selection handlers
    const handleCheckAvailableRow = (data) => {
        const rowSelected = checkedAvailable.some((a) => a.id === data.id);
        const newChecked = rowSelected
            ? checkedAvailable.filter((v) => v.id !== data.id)
            : [...checkedAvailable, data];

        handleCheckedAvailableChange(newChecked);
    };

    const handleCheckChosenRow = (data) => {
        const rowSelected = checkedChosen.some((a) => a.id === data.id);
        const newChecked = rowSelected
            ? checkedChosen.filter((v) => v.id !== data.id)
            : [...checkedChosen, data];

        handleCheckedChosenChange(newChecked);
    };

    // Move operations
    const handleMoveToChosen = () => {
        const newAvailable = differenceBy(
            availableData,
            checkedAvailable,
            'id',
        );
        const newChosen = [...chosenData, ...checkedAvailable];

        onAvailableDataChange?.(newAvailable);
        onChosenDataChange?.(newChosen);
        handleCheckedAvailableChange([]);
    };

    const handleMoveToAvailable = () => {
        const newChosen = differenceBy(chosenData, checkedChosen, 'id');
        const newAvailable = [...availableData, ...checkedChosen];

        onChosenDataChange?.(newChosen);
        onAvailableDataChange?.(newAvailable);
        handleCheckedChosenChange([]);
    };

    const handleMoveAllToChosen = () => {
        const newChosen = [...chosenData, ...availableData];
        const newAvailable = [];

        onChosenDataChange?.(newChosen);
        onAvailableDataChange?.(newAvailable);
        handleCheckedAvailableChange([]);
    };

    const handleMoveAllToAvailable = () => {
        const newAvailable = [...availableData, ...chosenData];
        const newChosen = [];

        onAvailableDataChange?.(newAvailable);
        onChosenDataChange?.(newChosen);
        handleCheckedChosenChange([]);
    };

    return (
        <Box className="flex flex-row justify-center">
            {/* Available Items Table */}
            <TransferTableProvider
                data={availableData}
                field={availableTableConfig.searchField}
                filterFunction={availableFilterFunction}>
                <TransferTable
                    checkedArray={checkedAvailable}
                    onClick={handleCheckAvailableRow}
                    data={availableData}
                    captionName={availableTableConfig.captionName}
                    placeholder={availableTableConfig.placeholder}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    colDefs={availableTableConfig.colDefs}
                    searchField={availableTableConfig.searchField}
                />
            </TransferTableProvider>

            <Box className="flex flex-col justify-center gap-4 p-4">
                <MoveButton
                    isDisabled={checkedAvailable.length === 0 || isDisabled}
                    dataWalkthroughId={walkthroughIds.moveToChosen}
                    handleOnClick={handleMoveToChosen}>
                    &gt;
                    <BadgeWithCount count={checkedAvailable.length} />
                </MoveButton>

                <MoveButton
                    isDisabled={checkedChosen.length === 0 || isDisabled}
                    dataWalkthroughId={walkthroughIds.moveToAvailable}
                    handleOnClick={handleMoveToAvailable}>
                    &lt;
                    <BadgeWithCount count={checkedChosen.length} />
                </MoveButton>

                <MoveButton
                    isDisabled={availableData.length === 0 || isDisabled}
                    dataWalkthroughId={walkthroughIds.moveAllToChosen}
                    handleOnClick={handleMoveAllToChosen}>
                    &gt;&gt;
                </MoveButton>

                <MoveButton
                    isDisabled={chosenData.length === 0 || isDisabled}
                    dataWalkthroughId={walkthroughIds.moveAllToAvailable}
                    handleOnClick={handleMoveAllToAvailable}>
                    &lt;&lt;
                </MoveButton>
            </Box>

            <TransferTableProvider
                data={chosenData}
                field={chosenTableConfig.searchField}
                filterFunction={chosenFilterFunction}>
                <TransferTable
                    checkedArray={checkedChosen}
                    onClick={handleCheckChosenRow}
                    data={chosenData}
                    captionName={chosenTableConfig.captionName}
                    placeholder={chosenTableConfig.placeholder}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    colDefs={chosenTableConfig.colDefs}
                    searchField={chosenTableConfig.searchField}
                />
            </TransferTableProvider>
        </Box>
    );
};

export default TransferringTables;
